<template>
  <div class="mobile-margin">
    <div class="d-flex justify-content-between align-items-center">
      <p class="reviewHeading">Reviews</p>
      <button
        class="r-btn"
        :style="{
          background: reviewed ? '#e2e8f0' : getPrimaryColor(),
          border: 'none !important',
          color: reviewed ? '#47556E' : 'white',
          'pointer-events': reviewed ? 'none' : 'auto',
        }"
        @click="handleReviewClick"
      >
        {{ reviewed ? "Reviewed" : "Review" }}
        {{
          treatment.treatment_type
            ? treatment.treatment_type === "others"
              ? "Medication"
              : treatment.treatment_type
            : "Others"
        }}
      </button>
    </div>

    <div class="d-flex mt-4 reviewHeadingBox" v-if="treatment">
      <div class="flex-center-center flex-column flex-grow-1">
        <div class="mt-4">
          <font-awesome-icon
            class="starbg"
            :icon="['fas', 'star']"
            :style="{ 'background-color': getPrimaryColor() }"
          />
          <span class="fw700 ml-2 gray900">
            {{ parseFloat(treatment.provider_rating || 0).toFixed(2) }}
          </span>
        </div>
        <p class="font14 mt-2 text-center gray900 px-4">
          based on <br class="show-in-mobile" />
          {{ treatment.provider_reviewer_count || 0 }} provider
          {{ treatment.provider_reviewer_count > 1 ? "reviews" : "review" }}
        </p>
      </div>
      <div class="separator"></div>
      <div class="flex-center-center flex-column flex-grow-1">
        <div class="mt-4">
          <font-awesome-icon
            class="starbg"
            :icon="['fas', 'star']"
            :style="{ 'background-color': getPrimaryColor() }"
          />
          <span class="fw700 ml-2 gray900">
            {{ parseFloat(treatment.user_rating || 0).toFixed(2) }}
          </span>
        </div>
        <p class="font14 mt-2 text-center gray900 px-4">
          based on <br class="show-in-mobile" />
          {{ treatment.user_reviewer_count || 0 }} user
          {{ treatment.user_reviewer_count > 1 ? "reviews" : "review" }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-content-center my-3">
      <div>
        <div class="spinner-border position-absolute spinner-class"></div>
      </div>
      <div :style="{ 'margin-left': '-14px', 'margin-top': '40px' }">
        Loading...
      </div>
    </div>

    <template v-else>
      <div
        class="d-flex flex-column shadow-card mt-3"
        v-for="(rev, index) in reviews"
        :key="index"
      >
        <div class="align-items-center d-flex">
          <img
            v-if="rev.reviewer[0].url"
            class="rounded-circle cp hw70"
            :src="rev.reviewer[0].url"
            alt="reviewer profile photo"
            @click="viewProfile(rev.reviewer[0])"
          />
          <img
            v-else-if="rev.reviewer[0].image"
            class="rounded-circle cp hw70"
            :src="rev.reviewer[0].image"
            alt="reviewer profile photo"
            @click="viewProfile(rev.reviewer[0])"
          />
          <div
            v-else
            class="rounded-circle cp hw70 flex-center-center font27 font-white"
            :style="{ 'background-color': getAvatarColor(rev.reviewer[0].id) }"
            @click="viewProfile(rev.reviewer[0])"
          >
            {{ getInitials(rev.reviewer_role, rev.reviewer[0]) }}
          </div>
          <div
            class="d-flex flex-column align-items-start flex-grow-1 ml-3 cp"
            @click="viewProfile(rev.reviewer[0])"
          >
            <p class="fw700 text-capitalize">
              {{ getDisplayName(rev.reviewer_role, rev.reviewer[0]) }}
            </p>
            <p
              v-if="
                [
                  'doctor',
                  'hospital_administrator',
                  'clinic_administrator',
                  'physician',
                  'ohcp',
                  'hcp',
                  'nfp_administrator',
                  'business_administrator',
                ].includes(rev.reviewer_role)
              "
              class="font12 fw700 revRole flex-center-center text-capitalize"
            >
              <font-awesome-icon
                :icon="['fal', 'user-md']"
                v-if="
                  rev.reviewer_role === 'doctor' ||
                  rev.reviewer_role === 'physician'
                "
              />
              <font-awesome-icon
                :icon="['fal', 'user-nurse']"
                v-if="
                  rev.reviewer_role === 'ohcp' || rev.reviewer_role === 'hcp'
                "
              />
              <font-awesome-icon
                :icon="['fal', 'hospital-alt']"
                v-if="rev.reviewer_role === 'hospital_administrator'"
              />
              <font-awesome-icon
                :icon="['fal', 'hospital']"
                v-if="rev.reviewer_role === 'clinic_administrator'"
              />
              <font-awesome-icon
                :icon="['far', 'hand-holding-heart']"
                v-if="rev.reviewer_role === 'nfp_administrator'"
              />
              <font-awesome-icon
                :icon="['far', 'store']"
                v-if="rev.reviewer_role === 'business_administrator'"
              />
              {{ getDisplayRole(rev.reviewer_role) }}
            </p>
            <p
              class="font12 fw700 revRole flex-center-center text-capitalize"
              :style="{
                color: getPrimaryColor(),
                background: getPrimary50Color(),
              }"
              v-if="rev.reviewer_role === 'caregiver'"
            >
              <font-awesome-icon :icon="['far', 'hands-heart']" /> Caregiver
            </p>
            <div
              class="d-flex gap8 advCont"
              v-if="rev.reviewer_role === 'advocate'"
            >
              <p
                class="font12 fw700 revRole flex-center-center text-capitalize text-left"
                :style="{
                  color: getPrimaryColor(),
                  background: getPrimary50Color(),
                }"
              >
                <font-awesome-icon :icon="['fas', 'bullhorn']" class="mr-1" />
                Advocate
              </p>
              <p
                v-if="rev.reviewer[0].organization"
                class="font12 fw700 revRole flex-center-center text-capitalize text-left bg-purple50 purple300"
              >
                Supporting {{ rev.reviewer[0].organization }}
              </p>
            </div>
            <p class="font12 gray400">
              {{ formatTime(rev.created_at) }}
            </p>
          </div>
          <div class="fw700 d-flex starNumber">
            <font-awesome-icon
              class="starbg mr-2"
              :icon="['fas', 'star']"
              :style="{ 'background-color': getPrimaryColor() }"
            />
            {{ parseFloat(rev.rating).toFixed(1) }}
          </div>
          <div @click="setCurrentReview(index)" class="p-2">
            <a-icon
              v-if="currentReviewIndex === index"
              class="font20 cp gray400"
              type="up"
              :size="'large'"
            />
            <a-icon
              v-else
              class="font20 cp gray400"
              type="down"
              :size="'large'"
            />
          </div>
        </div>
        <!-- expanded review details -->
        <div
          class="d-flex justify-content-between flex-column align-items-center"
          v-if="currentReviewIndex === index"
        >
          <p :class="`mt-3 reviewText gray500 w-100`">
            <span v-if="rev.description && seeMore">
              {{ rev.description }}
            </span>
            <span v-else-if="rev.description && !seeMore">
              {{ rev.description.substring(0, 200) + "..." }}
            </span>
            <span v-else>
              <em> No Description Available </em>
            </span>
          </p>
          <p
            v-if="!seeMore && rev.description"
            class="cp seeMoreText font14"
            @click="() => (seeMore = true)"
          >
            see more
          </p>
          <div class="d-flex align-items-center w-100 mt-4 flex-wrap gap8">
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.accessibility"
            >
              Accessibility
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.accessibility }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.adherence_by_patients"
            >
              Adherence by Patients
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.adherence_by_patients }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.affordability"
            >
              Affordability
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.affordability }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.availability_of_alternatives"
            >
              Availability of Alternatives
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.availability_of_alternatives }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.drug_interactions"
            >
              Drug Interactions
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.drug_interactions }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.ease_of_use"
            >
              Ease of use
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.ease_of_use }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.effectiveness"
            >
              Effectiveness
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.effectiveness }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.safety"
            >
              Safety
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.safety }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.side_effects"
            >
              Side Effects
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.side_effects }}
              </span>
            </div>
            <div
              class="flex-center-center eachIndividualRating"
              v-if="rev.tolerability"
            >
              Tolerability
              <span :style="{ color: getPrimaryColor() }">
                <a-icon class="font20 mr-2" type="star" :size="'large'" />
                {{ rev.tolerability }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center w-100 mt-4 helpful flex-wrap">
            <span class="gray900 one"> Was this review helpful? </span>
            <span class="gray900 two">
              <font-awesome-icon
                :icon="[
                  `${
                    rev.like_reaction && rev.like_reaction.length
                      ? 'fas'
                      : 'fal'
                  }`,
                  'thumbs-up',
                ]"
                class="cp"
                @click="reactReview('like', rev)"
              />
              <font-awesome-icon
                :icon="[
                  `${
                    rev.dislike_reaction && rev.dislike_reaction.length
                      ? 'fas'
                      : 'fal'
                  }`,
                  'thumbs-down',
                ]"
                class="ml-4 cp"
                @click="reactReview('dislike', rev)"
              />
            </span>
            <span
              class="gray900 ml-auto cp three"
              @click="reactReview('flag', rev)"
              v-if="
                !(
                  rev.reviewer_role === authUser.user.role &&
                  rev.reviewer_id === authUser.user.id
                )
              "
            >
              <font-awesome-icon
                :icon="[
                  `${
                    rev.flag_reaction && rev.flag_reaction.length
                      ? 'fas'
                      : 'fal'
                  }`,
                  'flag',
                ]"
                class="mr-2"
              />
              Flag Review
            </span>
          </div>
        </div>
      </div>
    </template>

    <a-pagination
      v-if="reviews.length && totalCount > 5"
      v-model="currentPage"
      show-less-items
      :pageSize="5"
      @change="handleChangePage"
      class="d-flex justify-content-center"
      :total="totalCount"
    />

    <medication-review
      ref="medRev"
      :treatment="treatment"
      :getMedReviews="getMedReviews"
      :getTreatmentDetails="getTreatmentDetails"
    />
  </div>
</template>

<script>
import { RepositoryFactory } from "../../../repositories/RepositoryFactory";
import MedicationReview from "../../Modals/MedicationReview.vue";
import { reactive } from "vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faBullhorn } from "@fortawesome/pro-solid-svg-icons";
import {
  faHandsHeart,
  faHandHoldingHeart,
} from "@fortawesome/pro-regular-svg-icons";

library.add(faBullhorn, faHandsHeart, faHandHoldingHeart);
const PostRepository = RepositoryFactory.get("posts");
const UserRepository = RepositoryFactory.get("user");

export default {
  data() {
    const allReactions = reactive({
      0: [""],
    });

    return {
      currentReviewIndex: null,
      seeMore: false,
      reviews: [],
      reviewed: null,
      allReactions,
      currentPage: 1,
      totalCount: 0,
      loading: false,
    };
  },

  props: { treatment: Object, getTreatmentDetails: Function },

  components: { MedicationReview },

  created() {
    this.getMedReviews();
  },

  watch: {
    treatment: {
      handler(value) {
        this.reviewed = value.user_reviewed;
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    getDisplayRole(role) {
      switch (role.toLowerCase()) {
        case "hospital_administrator":
          return "Hospital";
        case "clinic_administrator":
          return "Clinic";
        case "nfp_administrator":
          return "Non For Profit";
        case "hcp":
        case "ohcp":
          return "Advanced Practice Provider";
        default:
          return role;
      }
    },
    getMedReviews() {
      this.loading = true;
      UserRepository.getTreatmentReviews(
        this.currentCircle.id,
        this.currentPage,
        5,
        this.treatment.id,
        this.treatment.treatment_type
      )
        .then((res) => {
          this.loading = false;
          if (Array.isArray(res.data.data) && res.data.data.length === 0) {
            this.reviews = [];
          } else {
            this.reviews = res.data.data.data;
            this.totalCount = res.data.data.paginator
              ? res.data.data.paginator.total_count
              : 0;
          }
        })
        .catch((e) => {
          console.log(e);
          this.loading = false;
        });
    },
    handleChangePage(page) {
      this.currentPage = page;
      this.getMedReviews();
    },
    getDisplayName(reviewerRole, reviewerDetails) {
      switch (reviewerRole) {
        case "hospital_administrator":
        case "hospital":
          return reviewerDetails.hospital_name;
        case "clinic_administrator":
        case "clinic":
          return reviewerDetails.clinic_name;
        case "nfp":
        case "nfp_administrator":
          return reviewerDetails.nfp_name;
        case "doctor":
        case "physician":
        case "hcp":
        case "ohcp":
          return reviewerDetails.first_name + " " + reviewerDetails.last_name;
        // case "advocate":
        //   return reviewerDetails.first_name + " " + reviewerDetails.last_name
        default:
          return reviewerDetails.display_name || "~No Name~";
      }
    },
    getInitials(reviewerRole, reviewerDetails) {
      let f = "",
        l = "";
      switch (reviewerRole) {
        case "hospital_administrator":
        case "hospital":
          f = reviewerDetails.hospital_name[0];
          break;
        case "clinic_administrator":
        case "clinic":
          f = reviewerDetails.clinic_name[0];
          break;
        case "nfp":
        case "nfp_administrator":
          f = reviewerDetails.nfp_name[0];
          break;
        case "advocate":
          f = reviewerDetails.display_name.split(" ")[0][0] || "A";
          l =
            reviewerDetails.display_name.split(" ").slice(1).join(" ")[0] || "";
          break;
        case "doctor":
        case "physician":
        case "hcp":
        case "ohcp":
          f = reviewerDetails.first_name && reviewerDetails.first_name[0];
          l = reviewerDetails.last_name && reviewerDetails.last_name[0];
          break;
        case "patient":
        case "caregiver":
          f = reviewerDetails.display_name && reviewerDetails.display_name[0];
          break;
      }
      return f.toUpperCase() + l.toUpperCase();
    },
    setCurrentReview(ind) {
      this.currentReviewIndex = ind === this.currentReviewIndex ? null : ind;
      if (
        this.reviews[ind].description &&
        this.reviews[ind].description.length > 200
      ) {
        this.seeMore = false;
      } else {
        this.seeMore = true;
      }
    },
    handleReviewClick() {
      if (!this.reviewed) {
        this.$refs.medRev.show();
      } else {
        this.$notification.error({
          message: "Already Reviewed",
          description: "Cannot review again",
        });
      }
    },
    deleteReact(ids, rType, saveR = false, revId, rTypeId) {
      const headers = {
        Authorization: `Bearer ${this.authUser.user.token}`,
      };
      this.$http
        .delete(
          `${process.env.BASE_API_URL}/reactions/?ids=${ids.toString()}`,
          { headers }
        )
        .then((res) => {
          this.$notification.success({
            message: "Done",
            description: `Removed ${rType === "like" ? "dislike" : "like"}`,
          });
          if (saveR) {
            this.saveReact(rType, revId, rTypeId);
          } else {
            this.getMedReviews();
          }
        })
        .catch((err) => {
          this.$notification.error({
            message: "Error",
            description: `Cannot remove ${
              rType === "like" ? "dislike" : "like"
            }`,
          });
        });
    },
    saveReact(rType, revId, rTypeId) {
      const payload = {
        reacted_on_type: 11,
        reacted_on_id: revId,
        user: this.authUser.user.id,
        user_role: this.authUser.user.role || "",
        reaction_type: rTypeId,
      };
      PostRepository.saveReaction(this.currentCircle.id, payload)
        .then((res) => {
          this.$notification.success({
            message: "Successful",
            description: `${rType} has been saved`,
          });
          this.getMedReviews();
        })
        .catch((err) => {
          this.$notification.error({
            message: "Error",
            description: `Cannot ${rType}`,
          });
        });
    },
    reactReview(rType, rev) {
      switch (rType) {
        case "like":
          if (rev.like_reaction && rev.like_reaction.length) {
            this.deleteReact(rev.like_reaction[0].id, rType);
          } else {
            if (rev.dislike_reaction && rev.dislike_reaction.length) {
              this.deleteReact(
                rev.dislike_reaction[0].id,
                rType,
                true,
                rev.id,
                2
              );
            } else {
              this.saveReact(rType, rev.id, 2);
            }
          }
          break;
        case "dislike":
          if (rev.dislike_reaction && rev.dislike_reaction.length) {
            this.deleteReact(rev.dislike_reaction[0].id, rType);
          } else {
            if (rev.like_reaction && rev.like_reaction.length) {
              this.deleteReact(
                rev.like_reaction[0].id,
                rType,
                true,
                rev.id,
                11
              );
            } else {
              this.saveReact(rType, rev.id, 11);
            }
          }
          break;
        case "flag":
          if (rev.flag_reaction && rev.flag_reaction.length) {
            this.deleteReact(rev.flag_reaction[0].id, rType);
          } else {
            this.saveReact(rType, rev.id, 5);
          }
          break;
      }
    },
    formatTime(value) {
      const d = new Date(value);
      const now = new Date();
      const seconds = Math.round(
        Math.abs((now.getTime() - d.getTime()) / 1000)
      );
      const minutes = Math.round(Math.abs(seconds / 60));
      const hours = Math.round(Math.abs(minutes / 60));
      const days = Math.round(Math.abs(hours / 24));
      const months = Math.round(Math.abs(days / 30.416));
      const years = Math.round(Math.abs(days / 365));
      if (Number.isNaN(seconds)) {
        return "";
      } else if (seconds <= 45) {
        return "a few seconds ago";
      } else if (seconds <= 90) {
        return "a minute ago";
      } else if (minutes <= 45) {
        return minutes + " minutes ago";
      } else if (minutes <= 90) {
        return "an hour ago";
      } else if (hours <= 22) {
        return hours + " hours ago";
      } else if (hours <= 36) {
        return "a day ago";
      } else if (days <= 25) {
        return days + " days ago";
      } else if (days <= 45) {
        return "a month ago";
      } else if (days <= 345) {
        return months + " months ago";
      } else if (days <= 545) {
        return "a year ago";
      } else {
        // (days > 545)
        return years + " years ago";
      }
    },
    viewProfile(user) {
      this.loading = true;
      if (
        user.id === this.authUser.user.id &&
        user.role === this.authUser.user.role
      ) {
        this.routeToAuthProfile(this.authUser.user.role);
      } else {
        UserRepository.getOtherUserProfile(user, this.currentCircle.id)
          .then((res) => {
            const data = {
              ...res.data.data.user_info,
              on_circle: res.data.data.on_circle,
              request_to_add: res.data.data.request_to_add,
              no_of_members: res.data.data.no_of_members,
              recommendations: res.data.data.no_of_recommendations,
              recommend: res.data.data.recommend,
              overall_rating: res.data.data.overall_rating
                ? res.data.data.overall_rating
                : res.data.data.rating,
              inPersonReview: res.data.data.in_person_visit,
              onlineReview: res.data.data.online_tele_medicine,
              myCirclesReview: res.data.data.my_circle,
            };
            this.$store.commit("User/setViewUser", { data });
            switch (user.role) {
              case "doctor":
              case "physician":
                this.$router.push({
                  name: "ViewDoctorUpdates",
                  params: { id: user.id },
                });
                break;
              case "clinic_administrator":
                this.$router.push({
                  name: "ViewClinicAbout",
                  params: { id: user.id },
                });
                break;
              case "hospital_administrator":
                this.$router.push({
                  name: "ViewHos_adminAbout",
                  params: { id: user.id },
                });
                break;
              case "nfp":
                this.$router.push({
                  name: "ViewNfpAbout",
                  params: { id: user.id },
                });
                break;
              case "patient":
                this.$router.push({
                  name: "ViewPatientUpdates",
                  params: { id: user.id },
                });
                break;
              case "caregiver":
                this.$router.push({
                  name: "ViewPatientUpdates",
                  params: { id: user.id, role: "caregiver" },
                });
                break;
              case "advocate":
                this.$router.push({
                  name: "ViewAdvocateAbout",
                  params: { id: user.id },
                });
                break;
              case "hospital":
                this.$router.push({
                  name: "HospViewAbout",
                  params: { id: user.id },
                });
                break;
              case "clinic":
                this.$router.push({
                  name: "ClinicViewAbout",
                  params: { id: user.id },
                });
                break;
              case "nfp_administrator":
                this.$router.push({
                  name: "ViewNfpAdminAbout",
                  params: { id: user.id },
                });
                break;
              default:
                break;
            }
          })
          .catch((err) => {});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
img {
  object-fit: cover;
}
.starbg {
  // background: #2a71d4;
  border-radius: 2px;
  padding: 4px;
  color: #fff;
}
.shadow-card {
  p.gray400,
  p.seeMoreText {
    color: #94a3b8 !important;
  }
  p.revRole {
    color: #f5a623 !important;
  }
  p.gray500 {
    color: #647490 !important;
    span {
      color: #647490 !important;
    }
  }
  span.gray900 {
    color: #0f1734 !important;
  }
}
.reviewHeading {
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 18px;
  margin: auto 0;
  @media screen and (max-width: 900px) {
    margin-right: 24px;
  }
}
.r-btn {
  border-radius: 6px;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fff;
}
.separator {
  background: #cbd5e1;
  width: 1px;
  height: 80%;
  margin: auto 0;
}
.reviewHeadingBox {
  background: #f1f5f9;
  height: 110px;
}
.eachIndividualRating {
  padding: 8px;
  gap: 9px;
  // margin-right: 8px;
  // margin-bottom: 8px;
  background: #f1f5f9;
  border-radius: 8px;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  @media screen and (max-width: 900px) {
    // width: 48%;
    width: 100%;
    justify-content: space-between;
  }
}
.seeMoreText {
  line-height: 24px;
  align-self: flex-start;

  &:hover {
    color: #6d747e;
    text-decoration: underline;
  }
}
.revRole {
  line-height: 14px;
  text-align: center;
  background: #fff0d6;
  border-radius: 4px;
  padding: 4px 8px;
  gap: 4px;
}
.reviewText {
  overflow: hidden;
  text-overflow: ellipsis;
  transition: max-height all 0.5s;
}
.two {
  margin-left: 24px;
}
@media screen and (max-width: 900px) {
  .mobile-margin {
    margin: 0 16px;
  }
  .helpful {
    .one {
      width: 100%;
      margin-bottom: 24px;
    }
    .two {
      margin-left: 0px;
    }
  }
  .starNumber {
    flex-direction: column;
  }
  .revRole {
    max-width: 80%;
  }
  .advCont {
    width: 100%;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/home/treatments/Reviews.vue